import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, ChefHat, Minus, Plus, Flame, Ban, Lock, Crown, ShieldCheck, Repeat, Play } from "lucide-react";
import { CookingMode } from "@/components/CookingMode";
import { usePlanActions } from "@/lib/planActions";
import { ReplaceMealDialog } from "@/components/plan/ReplaceMealDialog";
import { FavoriteButton } from "@/components/FavoriteButton";
import { RecipeNotes } from "@/components/RecipeNotes";
import { Stars } from "@/components/Stars";
import { EatRecipeButton } from "@/components/journal/EatRecipeButton";

export default function RecipeDetail() {
  const { id } = useParams();
  const { t, tv, lang } = useLang();
  const nav = useNavigate();
  const actions = usePlanActions();
  const [portions, setPortions] = useState(1);
  const [replace, setReplace] = useState(false);
  const [cooking, setCooking] = useState(false);

  const { data: recipe, isLoading } = useQuery({
    queryKey: ["recipe", id],
    queryFn: async () => (await api.get(`/recipes/${id}`)).data,
  });

  const replaceTarget = useMemo(() => recipe ? { ...recipe, id: recipe.variant_of || recipe.id } : null, [recipe]);

  if (isLoading || !recipe) {
    return <AppShell><div className="h-64 grid place-items-center text-muted-foreground">{t("loading")}</div></AppShell>;
  }

  const f = portions;
  const nut = recipe.nutrition;
  const instr = recipe.instructions[lang] || recipe.instructions.en || recipe.instructions.fr;
  const diffKey = { easy: "easy", medium: "medium", hard: "hard" }[recipe.difficulty] || "easy";

  const fmtQty = (q) => {
    if (q == null) return "•••";
    const v = q * f;
    return Number.isInteger(v) ? v : v.toFixed(1).replace(/\.0$/, "");
  };

  return (
    <AppShell>
      <button onClick={() => nav(-1)} data-testid="recipe-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {t("back_to_plan")}
      </button>

      <div className="chenia-fade-up">
        <div className="relative rounded-[2rem] overflow-hidden h-56 sm:h-72">
          <img src={recipe.image} alt="" className="h-full w-full object-cover" style={{ objectPosition: recipe.focal || "50% 50%" }} data-testid="recipe-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-chenia-espresso/70 to-transparent" />
          <FavoriteButton recipeId={recipe.variant_of || recipe.id} className="absolute top-4 right-4" />
          <div className="absolute bottom-5 left-5 right-5 text-chenia-cream">
            <span className="rounded-full bg-chenia-cream/20 backdrop-blur px-3 py-1 text-xs font-semibold uppercase tracking-wide">{t(recipe.category)}</span>
            {recipe.tier && recipe.tier !== "free" && <span className={`ml-2 rounded-full px-3 py-1 text-xs font-bold tracking-wide inline-flex items-center gap-1 ${recipe.tier === "premium" ? "bg-primary" : "bg-chenia-sage text-foreground"}`} data-testid="recipe-premium-badge"><Crown className="h-3 w-3" /> {t(`tier_${recipe.tier}`)}</span>}
            {recipe.base_servings > 1 && <span className="ml-2 text-xs opacity-90">{t("base_servings")} {recipe.base_servings} {t("portions")}</span>}
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="recipe-title">{tv(recipe.name)}</h1>
            {recipe.note?.rating && <div className="mt-1"><Stars value={recipe.note.rating} testid="recipe-hero-rating" /></div>}
          </div>
        </div>

        {recipe.locked && (
          <div className="mt-5 chenia-card p-6 flex flex-wrap items-center gap-4 bg-gradient-to-br from-chenia-rosesoft to-card" data-testid="recipe-locked">
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-primary text-primary-foreground"><Lock className="h-5 w-5" /></span>
            <div className="flex-1 min-w-48">
              <p className="font-semibold">{t("tier_required")} {t(`tier_${recipe.tier}`)}</p>
              <p className="text-sm text-muted-foreground">{t("premium_locked_desc", { plan: t(`plan_${recipe.tier || "premium"}`) })}</p>
            </div>
            <Link to="/pricing"><Button className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="recipe-unlock">{t("unlock")}</Button></Link>
          </div>
        )}

        {recipe.variant_of && (
          <div className="mt-5 chenia-card p-5 border-chenia-sage/60" data-testid="recipe-variant">
            <p className="font-semibold inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> {t("variant_title")} — {t("variant_badge").toLowerCase()} {recipe.avoided_allergens.map((a) => t(`al_${a}`).toLowerCase()).join(", ")}</p>
            <ul className="mt-2 text-sm text-muted-foreground space-y-0.5">
              {recipe.substitutions.map((s) => <li key={`${s.allergen}-${tv(s.from)}`}>{tv(s.from)} → <span className="text-foreground font-medium">{tv(s.to)}</span></li>)}
            </ul>
            <p className="mt-2 text-xs text-chenia-terracottadark">{t("variant_notice")}</p>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-3 items-stretch">
          <div className="chenia-card px-4 py-3 flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /><div><p className="text-xs text-muted-foreground">{t("prep_time")}</p><p className="font-semibold">{recipe.prep_time} {t("min")}</p></div></div>
          <div className="chenia-card px-4 py-3 flex items-center gap-2"><ChefHat className="h-5 w-5 text-primary" /><div><p className="text-xs text-muted-foreground">{t("difficulty")}</p><p className="font-semibold">{t(diffKey)}</p></div></div>
          <div className="chenia-card px-4 py-3 flex items-center gap-2"><Flame className="h-5 w-5 text-primary" /><div><p className="text-xs text-muted-foreground">{t("calories")}{recipe.nutrition_approx ? " ≈" : ""}</p><p className="font-semibold">{nut ? `${Math.round(nut.calories * f)} ${t("kcal")}` : "—"}</p></div></div>
          {!recipe.locked && (
            <div className="flex flex-wrap gap-2 self-center ml-auto">
              <Button onClick={() => setCooking(true)} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="recipe-cooking-mode">
                <Play className="h-4 w-4 mr-1" /> {t("cooking_mode")}
              </Button>
              <Button variant="outline" onClick={() => setReplace(true)} className="rounded-full" data-testid="recipe-replace-meal">
                <Repeat className="h-4 w-4 mr-1" /> {t("replace_meal_with")}
              </Button>
              <EatRecipeButton recipe={recipe} portions={f} size="default" />
            </div>
          )}
        </div>
        {cooking && <CookingMode recipe={recipe} steps={instr} portions={f} fmtQty={fmtQty} onClose={() => setCooking(false)} />}
        <ReplaceMealDialog open={replace} onClose={() => setReplace(false)} recipe={replaceTarget} onDone={() => nav("/meal-plan")} />

        <div className="grid lg:grid-cols-5 gap-5 mt-6">
          {/* Ingredients + portions */}
          <div className="lg:col-span-2 space-y-5">
            <div className="chenia-card p-6" data-testid="recipe-portions">
              <div className="flex items-center justify-between">
                <span className="font-medium">{t("portions")}</span>
                <div className="flex items-center gap-3">
                  <button data-testid="portion-minus" onClick={() => setPortions(Math.max(0.5, f - 0.5))} className="grid place-items-center h-9 w-9 rounded-full border border-border hover:bg-secondary"><Minus className="h-4 w-4" /></button>
                  <span className="font-serif text-2xl font-semibold w-10 text-center" data-testid="portion-value">{f}</span>
                  <button data-testid="portion-plus" onClick={() => setPortions(f + 0.5)} className="grid place-items-center h-9 w-9 rounded-full border border-border hover:bg-secondary"><Plus className="h-4 w-4" /></button>
                </div>
              </div>
            </div>

            <div className="chenia-card p-6" data-testid="recipe-ingredients">
              <h2 className="text-xl font-semibold mb-4">{t("ingredients")}</h2>
              <ul className="space-y-2.5">
                {recipe.ingredients.map((ing) => (
                  <li key={ing.key} className="flex items-center justify-between gap-2 py-1 border-b border-border/50 last:border-0 group/ing">
                    <span>{tv(ing.name)}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">{fmtQty(ing.qty)} {ing.unit === "pc" ? t("dm_unit_pc") : ing.unit === "slice" ? t("dm_unit_slice") : ing.unit}</span>
                      <button title={t("exclude_ingredient")} data-testid={`exclude-ingredient-${ing.key}`}
                        onClick={() => actions.exclude(tv(ing.name))} disabled={actions.busy === "exclude"}
                        className="grid place-items-center h-7 w-7 rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors">
                        <Ban className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="chenia-card p-6" data-testid="recipe-nutrition">
              <h2 className="text-xl font-semibold mb-4">{t("nutrition_facts")}</h2>
              <div className="grid grid-cols-2 gap-3">
                {!nut && <p className="col-span-4 text-sm text-muted-foreground" data-testid="recipe-nutrition-unavailable">{t("nutrition_unavailable")}</p>}
                {nut && [["calories", Math.round(nut.calories * f), t("kcal")], ["protein", Math.round(nut.protein * f), "g"], ["carbs", Math.round(nut.carbs * f), "g"], ["fat", Math.round(nut.fat * f), "g"]].map(([k, v, u]) => (
                  <div key={k} className="rounded-2xl bg-secondary p-3 text-center">
                    <p className="font-serif text-2xl font-semibold">{v}<span className="text-sm text-muted-foreground ml-0.5">{u}</span></p>
                    <p className="text-xs text-muted-foreground">{t(k)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Instructions + household notes */}
          <div className="lg:col-span-3 space-y-5">
            <div className="chenia-card p-6" data-testid="recipe-instructions">
              <h2 className="text-xl font-semibold mb-4">{t("instructions")}</h2>
              <ol className="space-y-4">
                {recipe.locked && <li className="text-muted-foreground text-sm">{t("premium_locked_desc", { plan: t(`plan_${recipe.tier || "premium"}`) })}</li>}
                {instr.map((s, i) => (
                  <li key={`${i}-${s.slice(0, 24)}`} className="flex gap-4">
                    <span className="grid place-items-center h-8 w-8 shrink-0 rounded-full bg-primary text-primary-foreground font-semibold text-sm">{i + 1}</span>
                    <p className="pt-1 leading-relaxed">{s}</p>
                  </li>
                ))}
              </ol>
            </div>
            {!recipe.locked && <RecipeNotes recipeId={recipe.variant_of || recipe.id} note={recipe.note} />}
          </div>
        </div>
      </div>

      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{t("disclaimer")}</p>
    </AppShell>
  );
}
